import type { JobNodeDto, ParticipationEdgeStateRow } from '@/types';

export interface VehicleFlowCard {
  flowId: string;
  templateNodeId: number;
  effectiveJob?: JobNodeDto;
  attempts: JobNodeDto[];
  jobIds: Set<number>;
  label: string;
  joinPolicy: 'AnyLive' | 'AllLive';
  isStart: boolean;
  isEnd: boolean;
  vin?: string | null;
  subjectId?: number | null;
}

export interface VehicleTemplateNode {
  id: number;
  name: string;
  joinPolicy: 'AnyLive' | 'AllLive';
  isStart: boolean;
  isEnd: boolean;
}

export interface VehicleTemplateEdge {
  id: number;
  fromNodeId: number;
  toNodeId: number;
}

export interface ProjectedVehicleLink {
  id: string;
  source: string;
  target: string;
  fromJobId?: number;
  state: 'Waiting' | 'Live' | 'Dead';
  deadReason:
    | 'PredecessorFailed'
    | 'PredecessorCancelled'
    | 'ConditionUnmet'
    | null;
  conditionResults: null;
}

const identityOf = (card: VehicleFlowCard) => {
  const vin = card.vin || card.effectiveJob?.vin;
  if (vin) return `v:${vin}`;
  if (card.subjectId != null) return `s:${card.subjectId}`;
  return null;
};

const stateFromPredecessor = (status?: string) => {
  switch (status) {
    case 'Completed':
      return { state: 'Live' as const, deadReason: null };
    case 'Failed':
      return {
        state: 'Dead' as const,
        deadReason: 'PredecessorFailed' as const,
      };
    case 'CancelledByUser':
    case 'CancelledBySystem':
      return {
        state: 'Dead' as const,
        deadReason: 'PredecessorCancelled' as const,
      };
    default:
      return { state: 'Waiting' as const, deadReason: null };
  }
};

const orderEdges = (
  nodes: VehicleTemplateNode[],
  edges: VehicleTemplateEdge[],
) => {
  const incoming = new Map(nodes.map((node) => [node.id, 0]));
  edges.forEach((edge) => {
    incoming.set(edge.toNodeId, (incoming.get(edge.toNodeId) ?? 0) + 1);
  });
  const outgoing = new Map<number, VehicleTemplateEdge[]>();
  edges.forEach((edge) => {
    const list = outgoing.get(edge.fromNodeId) ?? [];
    list.push(edge);
    outgoing.set(edge.fromNodeId, list);
  });
  const queue = nodes
    .filter((node) => (incoming.get(node.id) ?? 0) === 0)
    .map((node) => node.id);
  const ordered: VehicleTemplateEdge[] = [];
  while (queue.length) {
    const nodeId = queue.shift()!;
    (outgoing.get(nodeId) ?? []).forEach((edge) => {
      ordered.push(edge);
      const remaining = (incoming.get(edge.toNodeId) ?? 1) - 1;
      incoming.set(edge.toNodeId, remaining);
      if (remaining === 0) queue.push(edge.toNodeId);
    });
  }
  return ordered.length === edges.length ? ordered : edges;
};

export const projectLoadPullVehicleSteps = ({
  cards,
  nodes,
  edges,
  participationEdgeStates = [],
}: {
  cards: VehicleFlowCard[];
  nodes: VehicleTemplateNode[];
  edges: VehicleTemplateEdge[];
  participationEdgeStates?: ParticipationEdgeStateRow[];
}) => {
  const cardsByTemplateId = new Map<number, VehicleFlowCard[]>();
  cards.forEach((card) => {
    const list = cardsByTemplateId.get(card.templateNodeId) ?? [];
    list.push({ ...card });
    cardsByTemplateId.set(card.templateNodeId, list);
  });
  const participation = new Map(
    participationEdgeStates.map((state) => [
      `${state.edgeId}:${state.jobChainSubjectId ?? ''}`,
      state,
    ]),
  );
  const links: ProjectedVehicleLink[] = [];

  orderEdges(nodes, edges).forEach((templateEdge) => {
    const sources = cardsByTemplateId.get(templateEdge.fromNodeId) ?? [];
    const vehicleSources = sources.filter((card) => identityOf(card));
    if (!vehicleSources.length) return;

    const targetTemplate = nodes.find(
      (node) => node.id === templateEdge.toNodeId,
    );
    if (!targetTemplate) return;
    const targets = cardsByTemplateId.get(templateEdge.toNodeId) ?? [];

    vehicleSources.forEach((source) => {
      const key = identityOf(source)!;
      const subjectId =
        source.subjectId ?? source.effectiveJob?.jobChainSubjectId ?? null;
      let target =
        targets.find((card) => identityOf(card) === key) ??
        (subjectId == null
          ? undefined
          : targets.find((card) => card.subjectId === subjectId));
      if (!target) {
        target = {
          flowId: `${targetTemplate.id}:${key}`,
          templateNodeId: targetTemplate.id,
          attempts: [],
          jobIds: new Set<number>(),
          label: targetTemplate.name,
          joinPolicy: targetTemplate.joinPolicy,
          isStart: targetTemplate.isStart,
          isEnd: targetTemplate.isEnd,
          vin: source.vin ?? source.effectiveJob?.vin ?? null,
          subjectId,
        };
        targets.push(target);
      }
      const scopedSubjectId = target.subjectId ?? subjectId;
      const scoped =
        scopedSubjectId == null
          ? undefined
          : participation.get(`${templateEdge.id}:${scopedSubjectId}`);
      const derived = stateFromPredecessor(source.effectiveJob?.status);
      const state = scoped?.state ?? derived.state;
      links.push({
        id: `${templateEdge.id}:${source.flowId}:${target.flowId}`,
        source: source.flowId,
        target: target.flowId,
        fromJobId: source.effectiveJob?.id,
        state,
        deadReason:
          state === 'Dead'
            ? derived.deadReason || (scoped ? 'ConditionUnmet' : null)
            : null,
        conditionResults: null,
      });
    });

    const kept = targets.filter(
      (card) => identityOf(card) || card.effectiveJob,
    );
    cardsByTemplateId.set(templateEdge.toNodeId, kept.length ? kept : targets);
  });

  return {
    cards: nodes.flatMap((node) => cardsByTemplateId.get(node.id) ?? []),
    links,
  };
};
